
import { useForm } from "react-hook-form";
import {
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useState, useEffect } from "react";
import { randomMaleLogo, randomFemaleLogo } from "../../utils/data";
import { StaticImageData } from "next/image";
import { useAppSelector } from "@/hooks/useAppSelector";
import { MobileuseFormContext } from "./MobileFormContext";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;

const MobileBasicDetails = ({ onNext }: { onNext: () => void }) => {
  const { finalData, setFinalData } = MobileuseFormContext();

  const { isAuthenticated } = useAppSelector((state) => state.auth);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    setError,
    watch,
    clearErrors,
  } = useForm({
    defaultValues: {
      firstName: finalData.FirstName || "",
      lastName: finalData.LastName || "",
      email: finalData.Email || "",
      phone: finalData.PhoneNumber || "",
      gender: finalData.Gender || "",
      dob: finalData.DOB || "",
      receiveWhatsApp: finalData.ReceiveWhatsApp || false,
      USER_IMAGE: finalData.USER_IMAGE || null,
    },
  });

  const [emailExists, setEmailExists] = useState(true);
  const [phoneExists, setPhoneExists] = useState(true);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingPhone, setLoadingPhone] = useState(false);

  const selectedGender = watch("gender");

  const importedImageToBlob = (importedImage: string): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", importedImage, true);
      xhr.responseType = "blob";

      xhr.onload = () => {
        if (xhr.status === 200 && xhr.response) {
          resolve(xhr.response as Blob);
        } else {
          reject(new Error("Failed to convert image to Blob."));
        }
      };

      xhr.onerror = () => {
        reject(new Error("Failed to convert image to Blob."));
      };

      xhr.send();
    });
  };

  const importImageAsFile = async (importedImageUrl: string) => {
    try {
      const blob = await importedImageToBlob(importedImageUrl);
      const file = new File([blob], "image.png", { type: "image/png" });
      setValue("USER_IMAGE", file);
    } catch (error) {
      console.error("Error importing image:", error);
    }
  };

  // Update USER_IMAGE when gender changes
  useEffect(() => {
    if (selectedGender) {
      const selectedImage =
        selectedGender === "Male"
          ? randomMaleLogo[Math.floor(Math.random() * randomMaleLogo.length)]
          : randomFemaleLogo[
              Math.floor(Math.random() * randomFemaleLogo.length)
            ];

      importImageAsFile(selectedImage.src);
    }
  }, [selectedGender, setValue]);

  // Prefill values when component mounts
  useEffect(() => {
    setValue("firstName", finalData.FirstName || "");
    setValue("lastName", finalData.LastName || "");
    setValue("email", finalData.Email || "");
    setValue("phone", finalData.PhoneNumber || "");
    setValue("gender", finalData.Gender || "");
    setValue("dob", finalData.DOB || "");
    setValue("receiveWhatsApp", finalData.ReceiveWhatsApp || false);

    let selectedImage: StaticImageData | string = "";

    if (finalData.Gender === "Male") {
      selectedImage =
        randomMaleLogo[Math.floor(Math.random() * randomMaleLogo.length)];
    } else {
      selectedImage =
        randomFemaleLogo[Math.floor(Math.random() * randomFemaleLogo.length)];
    }

    const imageUrl =
      typeof selectedImage === "string" ? selectedImage : selectedImage.src;

    if (imageUrl) {
      importImageAsFile(selectedImage.src);
    }
  }, [finalData, setValue]);

  const whatsapp = watch("receiveWhatsApp");

  const handleEmailBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const email = e.target.value.trim();
    if (!email) {
      setError("email", { type: "manual", message: "Email is required" });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("email", {
        type: "manual",
        message: "Please enter a valid email address",
      });
      return;
    }

    try {
      setLoadingEmail(true);
      const response = await fetch(
        `${API_BASE_URL}/singup_email_verify/${email}`
      );
      const data = await response.json();

      if (data.status === "error") {
        setEmailExists(true);
        setError("email", {
          type: "manual",
          message: "Email already registered with Cyclecircle",
        });
      } else {
        setEmailExists(false);
        clearErrors("email");
      }
    } catch (error) {
      console.error("Error checking email existence:", error);
      setError("email", {
        type: "manual",
        message: "Something went wrong. Please try again later.",
      });
    } finally {
      setLoadingEmail(false);
    }
  };

  const handlePhoneBlurs = async (e: React.FocusEvent<HTMLInputElement>) => {
    const phoneNumber = e.target.value.trim();
    handlePhoneBlur(phoneNumber);
  };

  const handlePhoneBlur = async (phonenumber: string) => {
    // const phone = e.target.value.replace(/\D/g, "").slice(0, 10);
    const phone = phonenumber;
    // setValue("phone", phone);

    if (!phone) {
      setError("phone", {
        type: "manual",
        message: "Phone number is required",
      });
      return;
    }

    if (phone.length !== 10) {
      setError("phone", {
        type: "manual",
        message: "Please enter a valid 10-digit phone number",
      });
      return;
    }

    try {
      setLoadingPhone(true);
      const response = await fetch(
        `${API_BASE_URL}/singup_phone_verify/${phone}`
      );
      const data = await response.json();

      if (data.status === "error") {
        setPhoneExists(true);
        setError("phone", {
          type: "manual",
          message: "Phone number already registered with Cyclecircle",
        });
      } else {
        setPhoneExists(false);
        clearErrors("phone");
      }
    } catch (error) {
      console.error("Error checking phone number existence:", error);
      setError("phone", {
        type: "manual",
        message: "Something went wrong. Please try again later.",
      });
    } finally {
      setLoadingPhone(false);
    }
  };

  const isFormValid = () => {
    return (
      watch("firstName")?.trim().length >= 3 &&
      watch("lastName")?.trim().length >= 3 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(watch("email")) &&
      watch("phone")?.length === 10 &&
      ["Male", "Female", "Other"].includes(watch("gender")) &&
      watch("dob")?.trim().length > 0 &&
      !emailExists &&
      !phoneExists
    );
  };

  useEffect(() => {
    if (finalData?.PhoneNumber !== null) {
      handlePhoneBlur(finalData?.PhoneNumber);
    }
    const email = watch("email"); // Get current email value from react-hook-form
    if (email) {
      handleEmailBlur({
        target: { value: email },
      } as React.FocusEvent<HTMLInputElement>);
    }
  }, [watch, finalData?.PhoneNumber]);

  const onSubmit = (data: any) => {
    if (emailExists || phoneExists) return;

    // Store data in context
    setFinalData({
      FirstName: data.firstName,
      LastName: data.lastName,
      Email: data.email,
      PhoneNumber: data.phone,
      Gender: data.gender,
      DOB: data.dob,
      ReceiveWhatsApp: data.receiveWhatsApp,
      USER_IMAGE: data.USER_IMAGE || null,
    });

    onNext();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="w-full max-h-full space-y-6"
    >
      <h2 className="text-2xl font-bold text-gray-900">Basic Details</h2>
      <span className="text-gray-500 text-sm tracking-wide">
        Please provide your personal information
      </span>

      <div className="grid grid-cols-2 gap-4">
        {/* First Name */}
        <TextField
          label="First Name"
          variant="outlined"
          fullWidth
          {...register("firstName", { required: "First name is required" })}
          error={!!errors.firstName}
          helperText={errors.firstName?.message}
        />

        {/* Last Name */}
        <TextField
          label="Last Name"
          variant="outlined"
          fullWidth
          {...register("lastName", { required: "Last name is required" })}
          error={!!errors.lastName}
          helperText={errors.lastName?.message}
        />
      </div>

      {/* Email */}
      <TextField
        label="Email"
        type="email"
        variant="outlined"
        fullWidth
        {...register("email", { required: "Email is required" })}
        error={!!errors.email}
        helperText={errors.email?.message}
        onBlur={handleEmailBlur}
        onInput={handleEmailBlur}
        InputProps={{
          endAdornment: loadingEmail ? (
            <CircularProgress size={20} sx={{ color: "var(--color-blue)" }} />
          ) : null,
        }}
      />
      {/* <input
        type="file"
        {...register("USER_IMAGE", { required: "Profile image is required" })}
        accept="image/*"
      /> */}

      {/* Phone Number */}
      <TextField
        label="Phone Number"
        variant="outlined"
        fullWidth
        {...register("phone", {
          required: "Phone number is required",
          pattern: {
            value: /^[0-9]{10}$/, // Ensures exactly 10 digits
            message: "Enter a valid 10-digit phone number",
          },
          setValueAs: (value) => value.replace(/\s/g, ""), // Remove spaces
        })}
        error={!!errors.phone}
        helperText={errors.phone?.message}
        onBlur={handlePhoneBlurs}
        InputProps={{
          inputMode: "numeric",
          endAdornment: loadingPhone ? (
            <CircularProgress size={20} sx={{ color: "var(--color-blue)" }} />
          ) : null,
        }}
        inputProps={{
          maxLength: 10,
        }}
        onKeyPress={(e) => {
          // Allow only numbers (0-9) and backspace (8)
          if (!/[0-9\b]/.test(e.key)) {
            e.preventDefault();
          }
        }}
        disabled={isAuthenticated}
      />

      <div className="grid grid-cols-2 gap-4">
        {/* Gender */}
        <TextField
          select
          label="Gender"
          variant="outlined"
          value={selectedGender}
          fullWidth
          {...register("gender", { required: "Gender is required" })}
          error={!!errors.gender}
          helperText={errors.gender?.message}
        >
          <MenuItem value="Male">Male</MenuItem>
          <MenuItem value="Female">Female</MenuItem>
          <MenuItem value="Other">Other</MenuItem>
        </TextField>

        {/* Date of Birth */}
        <TextField
          label="Date of Birth"
          type="date"
          variant="outlined"
          fullWidth
          InputLabelProps={{ shrink: true }}
          {...register("dob", {
            required: "Date of birth is required",
          })}
          InputProps={{
            inputProps: {
              max: new Date().toISOString().split("T")[0],
            },
          }}
          error={!!errors.dob}
          helperText={errors.dob?.message}
        />
      </div>
      <FormControlLabel
        control={
          <Checkbox
            value={whatsapp === true}
            {...register("receiveWhatsApp")}
          />
        }
        label={
          <span className="text-black-1 tracking-wide">
            Receive updates on WhatsApp
          </span>
        }
      />

      {/* Next Step Button */}
      <Button
        type="submit"
        variant="contained"
        color="primary"
        className="w-full"
        sx={{
          backgroundColor: "var(--color-blue)",
        }}
        disabled={!isFormValid()}
      >
        Next Step
      </Button>
    </form>
  );
};

export default MobileBasicDetails;
